import { motion } from "framer-motion";
import { useState } from "react";
import { useWindowSize } from "@/hooks/useWindowSize";

export const SOSBoard = ({ 
  board, 
  size, 
  onCellClick, 
  sosPatterns = [],
  disabled,
  currentPlayer,
  playerNames = ["Player 1", "Player 2"],
  playerCount = 2,
}) => {
  const [selectedLetter, setSelectedLetter] = useState('S');
  const [lightningCell, setLightningCell] = useState(null);
  const { width: winW, height: winH } = useWindowSize();

  const isSOSCell = (row, col) => {
    return sosPatterns.some(pattern => 
      pattern.some(([r, c]) => r === row && c === col)
    );
  };

  const getCellPx = () => {
    const headerSpace = 180;
    const letterBarSpace = 70;
    const padding = 30;
    const gapPx = 5;
    const maxBoardH = winH - headerSpace - letterBarSpace;
    const maxBoardW = Math.min(winW - 30, 900);
    const fromH = Math.floor((maxBoardH - padding - (size - 1) * gapPx) / size);
    const fromW = Math.floor((maxBoardW - padding - (size - 1) * gapPx) / size);
    const cell = Math.min(fromH, fromW);
    if (size <= 3) return Math.max(80, Math.min(cell, 140));
    if (size <= 5) return Math.max(60, Math.min(cell, 105));
    if (size <= 7) return Math.max(48, Math.min(cell, 80));
    if (size <= 9) return Math.max(38, Math.min(cell, 62));
    return Math.max(32, Math.min(cell, 50));
  };

  const cellPx = getCellPx();
  const fontSize = cellPx > 90 ? '4rem' : cellPx > 70 ? '3rem' : cellPx > 50 ? '2.2rem' : '1.5rem';

  const handleCellClick = (row, col) => {
    if (disabled || board[row][col]) return;
    setLightningCell(`${row}-${col}`);
    setTimeout(() => setLightningCell(null), 400);
    onCellClick(row, col, selectedLetter);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Letter Selection */}
      <div className="flex gap-4 items-center">
        <span className="font-fredoka text-gold-light text-sm sm:text-base">Pilih huruf:</span>
        <button
          onClick={() => setSelectedLetter('S')}
          disabled={disabled}
          data-testid="sos-letter-picker-S"
          className={`w-18 h-18 sm:w-22 sm:h-22 rounded-xl font-orbitron font-black text-4xl sm:text-5xl transition-all ${
            selectedLetter === 'S'
              ? 'bg-gradient-to-br from-gold to-yellow-500 text-brown-dark scale-110 shadow-[0_0_25px_rgba(255,215,0,0.5)]'
              : 'glass-card border-2 border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 hover:scale-105'
          }`}
          style={{ width: 72, height: 72 }}
        >
          S
        </button>
        <button
          onClick={() => setSelectedLetter('O')}
          disabled={disabled}
          data-testid="sos-letter-picker-O"
          className={`w-18 h-18 sm:w-22 sm:h-22 rounded-xl font-orbitron font-black text-4xl sm:text-5xl transition-all ${
            selectedLetter === 'O'
              ? 'bg-gradient-to-br from-orange to-red-400 text-brown-dark scale-110 shadow-[0_0_25px_rgba(255,107,53,0.5)]'
              : 'glass-card border-2 border-pink-400/50 text-pink-300 hover:bg-pink-400/10 hover:scale-105'
          }`}
          style={{ width: 72, height: 72 }}
        >
          O
        </button>
      </div>

      {/* Current Player Indicator */}
      <div className="text-center">
        <span className="font-fredoka text-gold-light text-sm sm:text-base">
          {playerNames[currentPlayer - 1] || `Player ${currentPlayer}`}'s turn - Place 
        </span>
        <span className={`font-orbitron font-bold ml-1 text-lg ${selectedLetter === 'S' ? 'text-gold' : 'text-orange'}`}>
          {selectedLetter}
        </span>
      </div>

      {/* Game Board */}
      <motion.div
        data-testid="sos-game-board"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative rounded-2xl p-3 sm:p-4"
        style={{ background: 'rgba(20,15,40,0.9)', boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)' }}
      >
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${size}, ${cellPx}px)`,
            gap: size <= 5 ? 6 : size <= 7 ? 5 : 4,
            justifyContent: 'center',
          }}
        >
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              const cellKey = `${rowIndex}-${colIndex}`;
              const isLightning = lightningCell === cellKey;
              const isSOS = isSOSCell(rowIndex, colIndex);
              
              return (
                <motion.button
                  key={cellKey}
                  data-testid={`sos-cell-${rowIndex}-${colIndex}`}
                  whileHover={!disabled && !cell ? { scale: 1.08, y: -2 } : {}}
                  whileTap={!disabled && !cell ? { scale: 0.93 } : {}}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                  disabled={disabled || !!cell}
                  style={{ width: cellPx, height: cellPx, fontSize }}
                  className={`
                    flex items-center justify-center
                    rounded-xl
                    font-orbitron font-black
                    transition-all duration-150
                    relative
                    ${!disabled && !cell 
                      ? "cursor-pointer border-2 border-gold/20 hover:border-gold/50" 
                      : "cursor-default border-2 border-gold/10"}
                    ${isSOS 
                      ? "sos-highlight border-yellow-400/60" 
                      : cell 
                        ? "bg-gradient-to-br from-white/10 to-white/5" 
                        : "bg-gradient-to-br from-gold/8 to-gold/3 hover:from-gold/15 hover:to-gold/8"}
                    ${isLightning ? "cell-flash" : ""}
                  `}
                  {...(!disabled && !cell ? { style: { width: cellPx, height: cellPx, fontSize, boxShadow: '0 3px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)' } } : { style: { width: cellPx, height: cellPx, fontSize, boxShadow: '0 2px 4px rgba(0,0,0,0.15)' } })}
                >
                  {cell === "S" && (
                    <motion.span
                      initial={{ scale: 0, opacity: 0, rotate: -90 }}
                      animate={{ scale: [0, 1.3, 1], opacity: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 12 }}
                      className="sos-symbol-s"
                    >
                      S
                    </motion.span>
                  )}
                  {cell === "O" && (
                    <motion.span
                      initial={{ scale: 0, opacity: 0, rotate: 90 }}
                      animate={{ scale: [0, 1.3, 1], opacity: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 12 }}
                      className="sos-symbol-o"
                    >
                      O
                    </motion.span>
                  )}
                </motion.button>
              );
            })
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SOSBoard;
